<script setup lang="ts">
import Tag from '@components/Tag.vue';
import type { Submenu } from '@global-types/nav-v2/menuItems';
import SubmenuItem from '@layout/nav-v2/SubmenuItem.vue';
import MenuItem from '@layout/nav-v2/desktop/components/MenuItem.vue';
import { computed } from 'vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import Icon from '@components/Icon.vue';
import MenuTooltip from '@layout/nav-v2/desktop/components/MenuTooltip.vue';
import IconCaretDown from '@icons/ph_caret-down.svg';
import Typography from '@components/Typography.vue';
import { trans } from '@elements/translation.ts';

interface MenuLinkProps {
  isDropdownOpen: boolean,
  isExpanded?: boolean,
  hasPin?: boolean,
  hasNewTag?: boolean,
  submenus: Submenu[],
}

const {
  isDropdownOpen: isDropdownOpenProp,
  isExpanded = false,
  hasPin = false,
  hasNewTag = false,
  submenus,
} = defineProps<MenuLinkProps>();

defineSlots<{
  icon(iconProps: { isActive: boolean }): typeof Icon,
  text(): unknown,
}>();

const emit = defineEmits<{(e: 'toggleDropdown', isOpen: boolean): void }>();

const { isCurrentPage } = useNavbarStore();

const isDropdownOpen = computed<boolean>({
  get: () => isDropdownOpenProp,
  set: (isOpen: boolean) => emit('toggleDropdown', isOpen),
});

const isActive = computed<boolean>(() => submenus.some((submenu) => isCurrentPage(submenu.link)));
</script>

<template>
  <div v-if="isExpanded">
    <MenuItem
      :is-active="isActive && !isDropdownOpen"
      :has-pin="hasPin"
      :is-expanded="isExpanded"
      @click="isDropdownOpen = !isDropdownOpen"
    >
      <template #icon>
        <slot name="icon" :is-active="isActive || isDropdownOpen" />
      </template>

      <template #text>
        <div class="flex items-center justify-between">
          <Typography
            :variant="isDropdownOpen ? 'body-small-bold' : 'body-small'"
            class="whitespace-nowrap"
          >
            <slot name="text" />
          </Typography>

          <div class="flex space-x-2">
            <Tag
              v-if="hasNewTag"
              text-color="purple"
              background-color="purple-lighter"
            >
              {{ trans('gestion.tag.new') }}
            </Tag>

            <Icon
              :svg="IconCaretDown"
              height="22"
              width="22"
              :fill="isActive ? 'black' : 'gray-darker'"
              :class="[
                'transition-transform',
                'duration-200',
                { 'rotate-180': isDropdownOpen },
              ]"
            />
          </div>
        </div>
      </template>
    </MenuItem>

    <ul
      v-if="isDropdownOpen"
      class="menu-item-group__dropdown-open"
      :class="[
        'ml-4',
        'border-l-2 border-solid border-gray-light',
        'p-2 pr-0',
        'flex flex-col space-y-2',
        'list-none',
      ]"
    >
      <li
        v-for="submenu in submenus"
        :key="submenu.name"
      >
        <SubmenuItem
          :link="submenu.link"
          :is-active="isCurrentPage(submenu.link)"
        >
          {{ submenu.name }}
        </SubmenuItem>
      </li>
    </ul>
  </div>

  <MenuTooltip v-else>
    <template #trigger>
      <MenuItem
        :is-active="isActive"
        :has-pin="hasPin"
        :is-expanded="isExpanded"
      >
        <template #icon>
          <Tag
            v-if="hasNewTag"
            text-color="purple"
            background-color="purple-lighter"
            class="absolute -top-4 left-1/2 -translate-x-1/2"
          >
            {{ trans('gestion.tag.new') }}
          </Tag>

          <slot name="icon" :is-active="isActive" />
        </template>

        <template #text>
          <slot name="text" />
        </template>
      </MenuItem>
    </template>

    <template #panel>
      <ul
        :class="[
          'list-none',
          'p-0',
          'flex flex-col space-y-1',
        ]"
      >
        <li
          v-for="submenu in submenus"
          :key="submenu.name"
        >
          <SubmenuItem
            :link="submenu.link"
            :is-active="isCurrentPage(submenu.link)"
          >
            {{ submenu.name }}
          </SubmenuItem>
        </li>
      </ul>
    </template>
  </MenuTooltip>
</template>
